import { useEffect, useRef } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Carousel } from "react-bootstrap";
import {
  FiGlobe,
  FiInstagram,
  FiLinkedin,
  FiExternalLink,
} from "react-icons/fi";
import { useLanguage } from "../i18n/LanguageContext";
import { projectsData } from "../data/projectsData";
import { projectCompanies } from "../data/projectCompanies";
import NotFound from "./NotFound";

const companyLinks = [
  { field: "website", icon: FiGlobe },
  { field: "linkedin", icon: FiLinkedin },
  { field: "instagram", icon: FiInstagram },
];
const normalizeUrl = (url) => url?.replace(/\/$/, "");

export default function ProjectDetails() {
  const { projectId } = useParams();
  const { state } = useLocation();
  const { t, localize } = useLanguage();
  const heading = useRef(null);
  const gallery = useRef(null);
  const source = projectsData.find((project) => project.id === projectId);
  const project = source && localize(source);
  const company = project && projectCompanies[project.companyId];
  const search = state?.projectsSearch ? `?${state.projectsSearch}` : "";

  useEffect(() => {
    heading.current?.focus();
    window.scrollTo(0, 0);
  }, [projectId]);

  // Preserve bookmarks when the admin case study is merged into the platform.
  if (projectId === "company-admin-dashboard") return <Navigate to="/projects/phinests-platform" state={state} replace />;
  if (!project) return <NotFound />;

  // Keep every existing image and video; navigation is manual so media stays readable.
  const media = [
    ...(project.images || []).map((image) => ({ ...image, type: "image" })),
    ...(project.videos || []).map((video) => ({ ...video, type: "video" })),
  ];
  if (!media.length) media.push({ ...project.cardImage, type: "image" });
  const next =
    projectsData[(projectsData.indexOf(source) + 1) % projectsData.length];
  const separateProjectLink =
    project.link &&
    normalizeUrl(project.link) !== normalizeUrl(company?.website);

  return (
    <main className="project-detail-page">
      <Link to={`/projects${search}`} className="project-text-link">
        {t("projects.details.back")}
      </Link>
      <div className="project-detail-heading">
        <div>
          <p className="projects-eyebrow">
            {t(`projects.filters.${project.category}`)}
          </p>
          <h1 ref={heading} tabIndex={-1}>
            {project.title}
          </h1>
        </div>
        <div className="project-context">
          <span>{t("projects.details.context")}</span>
          <p>
            {project.projectType || t(`projects.filters.${project.category}`)}
          </p>
          {separateProjectLink && (
            <a
              className="project-text-link"
              href={project.link}
              target="_blank"
              rel="noreferrer"
            >
              {project.linkPlaceHolder || t("projects.details.visit")}{" "}
              <FiExternalLink aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      <div ref={gallery} className="project-media">
        <Carousel
          key={project.id}
          interval={null}
          onSlide={() =>
            gallery.current
              ?.querySelectorAll("video")
              .forEach((video) => video.pause())
          }
          controls={media.length > 1}
          indicators={media.length > 1}
          indicatorLabels={media.map(
            (_, index) => `${t("projects.details.mediaItem")} ${index + 1}`,
          )}
          prevLabel={t("projects.details.previousImage")}
          nextLabel={t("projects.details.nextImage")}
        >
          {media.map((item, index) => (
            <Carousel.Item key={`${project.id}-${index}`}>
              {item.type === "video" ? (
                <video
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={item.alt}
                >
                  <source src={item.src} />
                  {item.alt}
                </video>
              ) : (
                <img
                  src={item.src}
                  alt={item.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              )}
            </Carousel.Item>
          ))}
        </Carousel>
        <p className="project-media-caption">
          {t("projects.details.gallery")} · {media.length}{" "}
          {t("projects.details.mediaItems")}
        </p>
      </div>
      {company && (
        <section
          className="project-company"
          aria-label={t("projects.details.company")}
        >
          <div>
            <p className="projects-eyebrow">
              {t(
                project.category === "freelance"
                  ? "projects.details.client"
                  : "projects.details.company",
              )}
            </p>
            <h2 dir={project.companyId === "creativityFestival2026" ? "ltr" : undefined}>
              {t(company.name).split(/(X)/).map((part, index) =>
                part === "X" ? (
                  <span className="pink-text" key={index}>
                    {part}
                  </span>
                ) : (
                  part
                ),
              )}
            </h2>
            <p>{project.projectType}</p>
          </div>
          <div className="project-company-links">
            {companyLinks
              .filter(({ field }) => company[field])
              .map(({ field, icon: Icon }) => (
                <a
                  key={field}
                  href={company[field]}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon aria-hidden="true" />
                  <span>{t(`projects.company.${field}`)}</span>
                  <FiExternalLink aria-hidden="true" />
                </a>
              ))}
          </div>
        </section>
      )}
      <div className="project-detail-content">
        <section>
          <h2>{t("projects.details.about")}</h2>
          <p className="project-full-description">{project.description}</p>
          {(project.contributionGroups?.length > 0 || project.descriptionPoints?.length > 0) && (
            <>
              <h3>{t("projects.details.contribution")}</h3>
              {project.contributionGroups ? project.contributionGroups.map(group => (
                <section className="project-contribution-group" key={group.title}>
                  <h4>{group.title}</h4>
                  <ul className="project-contributions">
                    {group.descriptionPoints.map(point => <li key={point}>{point}</li>)}
                  </ul>
                </section>
              )) : (
                <ul className="project-contributions">
                  {project.descriptionPoints.map((point, index) => <li key={index}>{point}</li>)}
                </ul>
              )}
            </>
          )}
        </section>
        <aside>
          <h2>{t("projects.technologies")}</h2>
          <ul className="project-tech-list">
            {project.technologies.map((tech) => (
              <li key={tech.iconName} dir="ltr">
                <span aria-hidden="true">{tech.icon}</span>
                {tech.iconName}
              </li>
            ))}
          </ul>
          {project.keyFeatures?.length > 0 && (
            <section>
              <h2>{t("projects.keyFeatures")}</h2>
              <ul className="project-features">
                {project.keyFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </section>
          )}
          {project.note && (
            <p className="project-note">
              <strong>{t("common.note")}</strong> {project.note}
            </p>
          )}
        </aside>
      </div>
      <div className="project-detail-bottom">
        <Link className="project-text-link" to={`/projects${search}`}>
          {t("projects.details.back")}
        </Link>
        <Link
          className="project-text-link"
          to={`/projects/${next.id}`}
          state={{ projectsSearch: state?.projectsSearch }}
        >
          {t("projects.details.nextProject")} <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </main>
  );
}
